export type PageLang = "ar" | "en";

export interface PageDescriptionContent {
  title: string;
  points: string[];
  steps?: string[];
}

export interface PageDescription {
  ar: PageDescriptionContent;
  en: PageDescriptionContent;
}

export const pageDescriptions: Record<string, PageDescription> = {
  "/dashboard": {
    ar: {
      title: "لوحة التحكم الرئيسية",
      points: [
        "نظرة عامة على إحصائيات الحضانة",
        "عدد الأطفال والموظفين والفصول",
        "ملخص الحضور اليومي",
        "آخر الأنشطة والتنبيهات",
      ],
      steps: [
        "راجع البطاقات العلوية لمعرفة الأرقام السريعة",
        "اضغط على أي بطاقة للانتقال لصفحة تفاصيلها",
        "تابع التنبيهات من جرس الإشعارات أعلى الصفحة",
      ],
    },
    en: {
      title: "Main Dashboard",
      points: [
        "Overview of nursery statistics",
        "Number of children, staff and classes",
        "Daily attendance summary",
        "Latest activities and alerts",
      ],
      steps: [
        "Review the top cards for quick numbers",
        "Click any card to open its details page",
        "Follow alerts from the bell icon at the top",
      ],
    },
  },
  "/dashboard/staff": {
    ar: {
      title: "إدارة الموظفين",
      points: [
        "إضافة وتعديل بيانات الموظفين والمعلمين",
        "إنشاء حسابات دخول للموظفين",
        "تحديد صلاحيات كل موظف",
        "عرض نموذج الراتب (ثابت / بالحصة / نسبة)",
      ],
      steps: [
        "اضغط 'إضافة موظف' وأدخل البيانات الأساسية",
        "حدد الدور (معلم / مشرف / محاسب) ونموذج الراتب",
        "فعّل الصلاحيات المطلوبة من قائمة الـ 17 صلاحية",
        "احفظ ليتم إنشاء حساب دخول تلقائياً",
      ],
    },
    en: {
      title: "Staff Management",
      points: [
        "Add and edit staff and teacher records",
        "Create login accounts for staff",
        "Set permissions per staff member",
        "Salary models (fixed / per session / percentage)",
      ],
      steps: [
        "Click 'Add Staff' and enter basic info",
        "Choose role (teacher / supervisor / accountant) and salary model",
        "Toggle the required permissions from the 17-item list",
        "Save — a login account is auto-created",
      ],
    },
  },
  "/dashboard/children": {
    ar: {
      title: "إدارة الأطفال",
      points: [
        "إضافة أطفال جدد (نموذج 4 خطوات)",
        "ربط الطفل بفصل وولي أمر وفرع",
        "استيراد جماعي من Excel",
        "عرض الملف الشامل لكل طفل",
      ],
      steps: [
        "اضغط 'إضافة طفل' أو 'استيراد Excel' للإضافة الجماعية",
        "أكمل خطوات النموذج: بيانات أساسية → ولي أمر → فصل → صحة",
        "اضغط على اسم الطفل لفتح ملفه الشامل",
        "استخدم البحث والفلاتر لإيجاد طفل معين بسرعة",
      ],
    },
    en: {
      title: "Children Management",
      points: [
        "Add new children (4-step form)",
        "Link child to a class, parent and branch",
        "Bulk import from Excel",
        "View the full profile of each child",
      ],
      steps: [
        "Click 'Add Child' or 'Import Excel' for bulk add",
        "Complete the form: basic → parent → class → health",
        "Click a child's name to open their full profile",
        "Use search and filters to find a child quickly",
      ],
    },
  },
  "/dashboard/classes": {
    ar: {
      title: "إدارة الفصول",
      points: [
        "إنشاء فصول وتعيين معلم لكل فصل",
        "جدول الحصص الأسبوعي",
        "نقل الطلاب بين الفصول",
        "تحليلات وإحصائيات كل فصل",
      ],
      steps: [
        "اضغط 'إضافة فصل' وحدد الاسم والفئة العمرية والمعلم",
        "افتح الفصل لإدارة جدول الحصص ونقل الطلاب",
        "راجع تبويب 'التحليلات' لمتابعة أداء الفصل",
      ],
    },
    en: {
      title: "Classes Management",
      points: [
        "Create classes and assign a teacher to each",
        "Weekly schedule of sessions",
        "Move students between classes",
        "Per-class analytics and statistics",
      ],
      steps: [
        "Click 'Add Class' and set name, age group and teacher",
        "Open the class to manage schedule and move students",
        "Check the 'Analytics' tab for class performance",
      ],
    },
  },
  "/dashboard/attendance": {
    ar: {
      title: "الحضور والغياب",
      points: [
        "تسجيل حضور الأطفال والموظفين يومياً",
        "مسح QR للحضور السريع",
        "تقارير الحضور الشهرية",
        "متابعة طلبات الإجازات",
      ],
      steps: [
        "اختر التاريخ والفصل من الأعلى",
        "سجّل الحضور يدوياً أو افتح ماسح QR للحضور السريع",
        "راجع تبويب 'طلبات الإجازات' للموافقة أو الرفض",
        "صدّر التقارير الشهرية من تبويب 'التقارير'",
      ],
    },
    en: {
      title: "Attendance",
      points: [
        "Daily attendance for children and staff",
        "QR scan for quick check-in",
        "Monthly attendance reports",
        "Manage leave requests",
      ],
      steps: [
        "Pick the date and class from the top",
        "Mark attendance manually or open the QR scanner",
        "Review 'Leave Requests' tab to approve or reject",
        "Export monthly reports from the 'Reports' tab",
      ],
    },
  },
  "/dashboard/lessons": {
    ar: {
      title: "الدروس والامتحانات",
      points: [
        "إنشاء دروس مع محتوى نصي وفيديو وملفات",
        "كراسة إلكترونية ذكية بشرح AI",
        "بنك أسئلة وإنشاء امتحانات",
        "أنشطة تفاعلية مرتبطة بكل درس",
      ],
      steps: [
        "اضغط 'إضافة درس' وحدد الفصل والعنوان",
        "أضف المحتوى: نص، فيديو، ملفات، أو شرائح AI",
        "اربط الدرس بأنشطة تفاعلية أو واجبات",
        "أنشئ امتحان من الدرس مباشرة عبر 'امتحان ذكي'",
      ],
    },
    en: {
      title: "Lessons & Exams",
      points: [
        "Create lessons with text, video and files",
        "Smart e-book with AI explanations",
        "Question bank and exam builder",
        "Interactive activities linked to each lesson",
      ],
      steps: [
        "Click 'Add Lesson' and select class and title",
        "Add content: text, video, files or AI slides",
        "Link the lesson to interactive activities or assignments",
        "Generate an exam directly from the lesson via 'Smart Exam'",
      ],
    },
  },
  "/dashboard/assignments": {
    ar: {
      title: "الواجبات",
      points: [
        "إنشاء واجبات مرتبطة بدرس أو فصل",
        "متابعة تسليمات الطلاب",
        "تصحيح الواجبات وإضافة ملاحظات",
        "عرض حالة التسليم (مسلّم / متأخر / لم يسلّم)",
      ],
      steps: [
        "اضغط 'إضافة واجب' وحدد الفصل وآخر موعد للتسليم",
        "ارفق ملف أو اكتب التعليمات للطلاب",
        "افتح الواجب لرؤية التسليمات وتصحيحها",
        "أضف الدرجة والملاحظات لكل طالب",
      ],
    },
    en: {
      title: "Assignments",
      points: [
        "Create assignments linked to a lesson or class",
        "Track student submissions",
        "Grade assignments and add feedback",
        "See submission status (submitted / late / missing)",
      ],
      steps: [
        "Click 'Add Assignment' and set class and deadline",
        "Attach a file or write instructions for students",
        "Open the assignment to view and grade submissions",
        "Add a grade and feedback for each student",
      ],
    },
  },
  "/dashboard/live-sessions": {
    ar: {
      title: "البث المباشر",
      points: [
        "إنشاء جلسات بث مباشر (Zoom / Meet)",
        "ربط الجلسة بفصل ودرس",
        "مشاركة رابط البث مع الطلاب",
        "حفظ رابط التسجيل بعد الجلسة",
      ],
      steps: [
        "اضغط 'جلسة جديدة' وأدخل العنوان والموعد",
        "ألصق رابط Zoom أو Meet واحفظ",
        "سيصل إشعار تلقائي لأولياء الأمور بالموعد",
        "أضف رابط التسجيل بعد انتهاء الجلسة",
      ],
    },
    en: {
      title: "Live Sessions",
      points: [
        "Create live sessions (Zoom / Meet)",
        "Link the session to a class and lesson",
        "Share the live link with students",
        "Save the recording link after the session",
      ],
      steps: [
        "Click 'New Session' and enter title and time",
        "Paste the Zoom or Meet link and save",
        "Parents are notified automatically with the schedule",
        "Add the recording link once the session ends",
      ],
    },
  },
  "/dashboard/interactive-learning": {
    ar: {
      title: "التعلّم التفاعلي",
      points: [
        "ألعاب تعليمية: رياضيات، عربي، إنجليزي، علوم",
        "فونكس إنجليزي (أصوات الحروف)",
        "حساب ذهني بمستويات متدرجة",
        "تقارير نتائج الألعاب لكل طفل",
      ],
      steps: [
        "اختر اللعبة المناسبة من القائمة",
        "حدد المستوى ومدة اللعبة",
        "تابع نتائج الأطفال في 'تقارير الألعاب'",
      ],
    },
    en: {
      title: "Interactive Learning",
      points: [
        "Educational games: math, Arabic, English, science",
        "English phonics (letter sounds)",
        "Mental math with progressive levels",
        "Per-child game result reports",
      ],
      steps: [
        "Pick the right game from the list",
        "Set the level and game duration",
        "Track children's results in 'Game Reports'",
      ],
    },
  },
  "/dashboard/reports": {
    ar: {
      title: "التقارير",
      points: [
        "تقارير أسبوعية تلقائية لكل طفل",
        "تقرير شهري شامل بالدرجات والحضور",
        "مقارنة أداء الطلاب",
        "تصدير التقارير كـ PDF",
      ],
      steps: [
        "اختر الطفل والفترة الزمنية",
        "راجع التقرير على الشاشة",
        "اضغط 'تصدير PDF' لحفظ نسخة قابلة للطباعة",
      ],
    },
    en: {
      title: "Reports",
      points: [
        "Automatic weekly reports per child",
        "Comprehensive monthly report (grades + attendance)",
        "Compare student performance",
        "Export reports as PDF",
      ],
      steps: [
        "Choose the child and time period",
        "Review the report on screen",
        "Click 'Export PDF' to save a printable copy",
      ],
    },
  },
  "/dashboard/analytics": {
    ar: {
      title: "تحليلات الأداء",
      points: [
        "رسوم بيانية لأداء كل طفل",
        "تتبع تطور الدرجات عبر الزمن",
        "مقارنة الحضور والسلوك",
        "تحليل نقاط القوة والضعف",
      ],
      steps: [
        "اختر الطفل لعرض تحليلات أدائه",
        "بدّل بين الرسوم البيانية المختلفة",
        "اطلب تحليل AI لاقتراحات تطوير الطفل",
      ],
    },
    en: {
      title: "Performance Analytics",
      points: [
        "Charts of each child's performance",
        "Track grade progression over time",
        "Compare attendance and behavior",
        "Analyze strengths and weaknesses",
      ],
      steps: [
        "Choose a child to view their analytics",
        "Switch between the different charts",
        "Request AI analysis for growth suggestions",
      ],
    },
  },
  "/dashboard/parents": {
    ar: {
      title: "إدارة أولياء الأمور",
      points: [
        "إنشاء حسابات أولياء الأمور",
        "ربط الأطفال بأولياء أمورهم",
        "متابعة حالة الدفع (مدفوع / متأخر / معلق)",
        "إرسال رسائل وتذكيرات جماعية",
      ],
      steps: [
        "اضغط 'إضافة ولي أمر' وأدخل البيانات",
        "اربط الأطفال به من 'ربط الأطفال'",
        "استخدم 'إرسال رسالة' للتواصل الجماعي أو الفردي",
        "تابع حالة الفواتير من العمود المخصص",
      ],
    },
    en: {
      title: "Parents Management",
      points: [
        "Create parent accounts",
        "Link children to their parents",
        "Track payment status (paid / late / pending)",
        "Send bulk messages and reminders",
      ],
      steps: [
        "Click 'Add Parent' and enter the details",
        "Link children to them via 'Link Children'",
        "Use 'Send Message' for bulk or one-to-one contact",
        "Track invoice status in the dedicated column",
      ],
    },
  },
  "/dashboard/billing": {
    ar: {
      title: "المحاسبة والفواتير",
      points: [
        "إنشاء فواتير لكل طفل",
        "متابعة حالة الدفع",
        "قوالب رسوم قابلة للتخصيص",
        "مراجعة إيصالات التحويل وقبول/رفض المدفوعات",
      ],
      steps: [
        "اضغط 'إنشاء فاتورة' أو استخدم قوالب الرسوم",
        "حدد الطفل والمبلغ وتاريخ الاستحقاق",
        "راجع الإيصالات المرفوعة وأكّد الدفع أو ارفضه",
        "صدّر تقرير الفواتير الشهري عند الحاجة",
      ],
    },
    en: {
      title: "Billing & Invoices",
      points: [
        "Create invoices per child",
        "Track payment status",
        "Customizable fee templates",
        "Review transfer receipts and approve/reject payments",
      ],
      steps: [
        "Click 'Create Invoice' or use a fee template",
        "Pick child, amount and due date",
        "Review uploaded receipts and confirm or reject payment",
        "Export the monthly invoices report when needed",
      ],
    },
  },
  "/dashboard/salary-report": {
    ar: {
      title: "تقرير الرواتب",
      points: [
        "حساب رواتب الموظفين الشهرية",
        "إضافة بدلات وخصومات",
        "دعم أنواع الدفع: ثابت / بالحصة / نسبة",
        "أرشفة سجلات الرواتب",
      ],
      steps: [
        "اختر الشهر والسنة من الأعلى",
        "راجع الرواتب المحسوبة تلقائياً",
        "أضف بدلات أو خصومات لكل موظف",
        "اضغط 'تأكيد الدفع' لأرشفة الراتب",
      ],
    },
    en: {
      title: "Salary Report",
      points: [
        "Calculate monthly staff salaries",
        "Add bonuses and deductions",
        "Supports fixed / per-session / percentage models",
        "Archive salary records",
      ],
      steps: [
        "Choose the month and year at the top",
        "Review the auto-calculated salaries",
        "Add bonuses or deductions per staff member",
        "Click 'Confirm Payment' to archive the salary",
      ],
    },
  },
  "/dashboard/financial-ledger": {
    ar: {
      title: "المركز المالي",
      points: [
        "نظرة شاملة: إيرادات، مصروفات، صافي ربح",
        "متابعة الفواتير المدفوعة والمتأخرة",
        "سجل رواتب الموظفين",
        "سجل كامل لجميع المعاملات المالية",
      ],
      steps: [
        "راجع البطاقات العلوية للأرقام السريعة",
        "أضف مصروف جديد من زر 'إضافة مصروف'",
        "بدّل بين تبويبات: الإيرادات، المصروفات، الرواتب",
        "صدّر السجل كاملاً للمحاسب الخارجي",
      ],
    },
    en: {
      title: "Financial Ledger",
      points: [
        "Full overview: revenue, expenses, net profit",
        "Track paid and overdue invoices",
        "Staff salary log",
        "Complete log of all financial transactions",
      ],
      steps: [
        "Review the top cards for quick numbers",
        "Add a new expense from the 'Add Expense' button",
        "Switch between: Revenue, Expenses, Salaries tabs",
        "Export the full ledger for the external accountant",
      ],
    },
  },
  "/dashboard/subscription": {
    ar: {
      title: "إدارة الاشتراك",
      points: [
        "عرض الباقة الحالية وتاريخ الانتهاء",
        "الترقية بين الباقات (Starter / Growth / Pro)",
        "اشتراك بالطفل (حسب عدد الأطفال)",
        "متابعة حالة الاشتراك والتجديد",
      ],
      steps: [
        "راجع الباقة الحالية وتاريخ الانتهاء",
        "قارن بين الباقات واختر المناسب",
        "اضغط 'ترقية' وأكّد طلب الاشتراك",
        "ارفع إيصال التحويل في انتظار التفعيل",
      ],
    },
    en: {
      title: "Subscription",
      points: [
        "View current plan and expiry date",
        "Upgrade between plans (Starter / Growth / Pro)",
        "Per-child plan (based on child count)",
        "Track subscription status and renewal",
      ],
      steps: [
        "Review current plan and expiry date",
        "Compare plans and choose the right one",
        "Click 'Upgrade' and confirm the request",
        "Upload the transfer receipt and wait for activation",
      ],
    },
  },
  "/dashboard/support": {
    ar: {
      title: "الدعم الفني",
      points: [
        "إرسال تذاكر دعم فني",
        "متابعة حالة التذاكر السابقة",
        "التواصل مع فريق الدعم",
        "عرض الردود والحلول",
      ],
      steps: [
        "اضغط 'تذكرة جديدة' واكتب الموضوع والمشكلة",
        "حدد الأولوية (عادي / عالي / عاجل)",
        "تابع الردود من قائمة التذاكر",
        "أو استخدم زر واتساب للتواصل المباشر",
      ],
    },
    en: {
      title: "Technical Support",
      points: [
        "Submit support tickets",
        "Track previous tickets",
        "Contact the support team",
        "View replies and solutions",
      ],
      steps: [
        "Click 'New Ticket' and write subject and issue",
        "Set priority (normal / high / urgent)",
        "Follow replies from the tickets list",
        "Or use the WhatsApp button for direct contact",
      ],
    },
  },
  "/dashboard/observations": {
    ar: {
      title: "متابعة الأطفال",
      points: [
        "تسجيل ملاحظات صحية ونفسية وسلوكية",
        "تصنيف المشاعر (إيجابي / سلبي / محايد)",
        "تحليل سلوكي بالذكاء الاصطناعي",
        "تتبع تطور الطفل عبر الزمن",
      ],
      steps: [
        "اختر الطفل واضغط 'إضافة ملاحظة'",
        "حدد التصنيف (صحي / نفسي / سلوكي) والمشاعر",
        "اكتب الملاحظة بتفاصيل واضحة",
        "اطلب 'تحليل AI' لاقتراحات تربوية",
      ],
    },
    en: {
      title: "Child Observations",
      points: [
        "Log health, psychological and behavioral notes",
        "Classify mood (positive / negative / neutral)",
        "AI-powered behavior analysis",
        "Track child progress over time",
      ],
      steps: [
        "Choose the child and click 'Add Observation'",
        "Select category (health / psychological / behavioral) and mood",
        "Write the note with clear details",
        "Request 'AI Analysis' for parenting suggestions",
      ],
    },
  },
  "/dashboard/activities": {
    ar: {
      title: "الأنشطة التكميلية",
      points: [
        "أنشطة سلوكية واجتماعية وصحية وإبداعية",
        "تلوين تفاعلي ومطابقة صور وألغاز",
        "نظام شارات ومستويات تحفيزي",
        "تتبع الأهداف اليومية (3 أنشطة/يوم)",
      ],
      steps: [
        "اختر الطفل من القائمة العلوية",
        "اختر نوع النشاط من التبويبات",
        "أكمل النشاط لجمع النقاط والشارات",
        "راجع 'سجل الأنشطة' لمتابعة التقدم",
      ],
    },
    en: {
      title: "Extra Activities",
      points: [
        "Behavioral, social, health and creative activities",
        "Interactive coloring, image matching and puzzles",
        "Motivational badges and levels system",
        "Daily goals tracking (3 activities/day)",
      ],
      steps: [
        "Pick the child from the top list",
        "Choose the activity type from the tabs",
        "Complete the activity to collect points and badges",
        "Check the 'Activity Log' to follow progress",
      ],
    },
  },
  "/dashboard/settings": {
    ar: {
      title: "الإعدادات",
      points: [
        "تعديل بيانات الحضانة والشعار",
        "إدارة الفروع",
        "إعدادات الحساب الشخصي",
        "تغيير كلمة المرور",
      ],
      steps: [
        "حدّث بيانات الحضانة والشعار من التبويب الأول",
        "أضف فروع جديدة من تبويب 'الفروع'",
        "غيّر كلمة المرور من تبويب 'الحساب'",
      ],
    },
    en: {
      title: "Settings",
      points: [
        "Edit nursery info and logo",
        "Manage branches",
        "Personal account settings",
        "Change password",
      ],
      steps: [
        "Update nursery info and logo from the first tab",
        "Add new branches from the 'Branches' tab",
        "Change your password from the 'Account' tab",
      ],
    },
  },
  "/dashboard/parent": {
    ar: {
      title: "لوحة ولي الأمر",
      points: [
        "اختيار الطفل لعرض بياناته",
        "متابعة الحضور والدرجات والواجبات",
        "عرض الفواتير ورفع إيصالات الدفع",
        "الدخول للوحة الطالب التفاعلية",
      ],
      steps: [
        "اختر 'لوحة ولي الأمر' لمتابعة التقارير",
        "أو اختر الطفل للدخول للوحة الطالب التفاعلية",
        "تنقّل بين تبويبات: الحضور، الواجبات، الفواتير، الرسائل",
      ],
    },
    en: {
      title: "Parent Dashboard",
      points: [
        "Pick a child to view their data",
        "Track attendance, grades and assignments",
        "View invoices and upload payment receipts",
        "Enter the interactive student portal",
      ],
      steps: [
        "Choose 'Parent Dashboard' to follow reports",
        "Or pick a child to enter the interactive student portal",
        "Move between tabs: Attendance, Assignments, Invoices, Messages",
      ],
    },
  },
  "/dashboard/parent-home": {
    ar: {
      title: "الصفحة الرئيسية لولي الأمر",
      points: [
        "ملخص يومي لأنشطة الطفل",
        "آخر الدروس والواجبات",
        "حالة الحضور اليوم",
        "الفواتير المستحقة",
      ],
      steps: [
        "راجع الملخص اليومي في الأعلى",
        "اضغط على أي بطاقة للانتقال لتفاصيلها",
        "ادفع الفواتير المستحقة من البطاقة المخصصة",
      ],
    },
    en: {
      title: "Parent Home",
      points: [
        "Daily summary of the child's activities",
        "Latest lessons and assignments",
        "Today's attendance status",
        "Due invoices",
      ],
      steps: [
        "Review the daily summary at the top",
        "Click any card to open its details",
        "Pay due invoices from the dedicated card",
      ],
    },
  },
  "/dashboard/game-reports": {
    ar: {
      title: "تقارير الألعاب",
      points: [
        "نتائج الألعاب التعليمية لكل طفل",
        "النجوم والدرجات المحققة",
        "الوقت المستغرق في كل لعبة",
        "تتبع التقدم عبر الزمن",
      ],
      steps: [
        "اختر الطفل والفترة الزمنية",
        "راجع النتائج التفصيلية لكل لعبة",
        "قارن الأداء عبر الأسابيع لمتابعة التطور",
      ],
    },
    en: {
      title: "Game Reports",
      points: [
        "Educational game results per child",
        "Stars and scores earned",
        "Time spent in each game",
        "Progress tracking over time",
      ],
      steps: [
        "Choose the child and time period",
        "Review detailed results per game",
        "Compare performance across weeks to track growth",
      ],
    },
  },
};

export function getPageDescription(
  path: string,
  lang: PageLang = "ar"
): PageDescriptionContent | null {
  const pick = (entry: PageDescription) => entry[lang] ?? entry.ar;

  // Exact match first
  if (pageDescriptions[path]) return pick(pageDescriptions[path]);

  // Try matching parent paths for dynamic routes
  for (const key of Object.keys(pageDescriptions)) {
    if (path.startsWith(key) && key !== "/dashboard") {
      return pick(pageDescriptions[key]);
    }
  }

  return pageDescriptions["/dashboard"] ? pick(pageDescriptions["/dashboard"]) : null;
}
